import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Instagram, Facebook, Youtube, Linkedin, Music2, Landmark } from "lucide-react";

const COLS = [
  { title: "KLEDIO", links: [["Über uns", "/ueber-uns"], ["So funktioniert KLEDIO", "/so-funktionierts"], ["Nachhaltigkeit", "/nachhaltigkeit"], ["Barrierefreiheit", "/barrierefreiheit"], ["Kontakt", "/kontakt"]] },
  { title: "Entdecken", links: [["Alle Kategorien", "/kategorien"], ["Neu eingestellt", "/neu"], ["Beliebte Artikel", "/beliebt"], ["Artikel suchen", "/suche"], ["Häufige Fragen (FAQ)", "/faq"]] },
  { title: "Hilfe", links: [["Hilfe-Center", "/hilfe"], ["Käuferschutz", "/kaeuferschutz"], ["Rückerstattungen", "/rueckerstattungen"], ["Sicherheit", "/sicherheit"], ["Meldestelle", "/meldestelle"], ["Kontakt", "/kontakt"]] },
  { title: "Für Verkäufer", links: [["Verkaufen auf KLEDIO", "/verkaufen-auf-kledio"], ["Artikel einstellen", "/verkaufen"], ["Gebühren", "/gebuehren"], ["Auszahlungen", "/auszahlungen"], ["Verkäufer-Badges", "/badges"], ["Werbung & Boosts", "/werbung"]] },
  { title: "Rechtliches", links: [["AGB", "/agb"], ["Datenschutz", "/datenschutz"], ["Impressum", "/impressum"], ["Verkäuferbedingungen", "/verkaeuferbedingungen"], ["Markenschutz & Urheberrecht", "/markenschutz"]] },
];

function Col({ col }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border md:border-0 py-3 md:py-0">
      <button onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full flex items-center justify-between md:pointer-events-none text-sm font-bold" data-testid={`footer-col-${col.title}`}>
        {col.title}
        <ChevronDown className={`w-4 h-4 md:hidden transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <ul className={`mt-3 space-y-2 ${open ? "block" : "hidden"} md:block`}>
        {col.links.map(([label, to]) => (
          <li key={label + to}><Link to={to} className="text-sm text-muted-foreground hover:text-accent transition-colors">{label}</Link></li>
        ))}
      </ul>
    </div>
  );
}

export const Footer = () => (
  <footer className="mt-10 md:mt-16 bg-white border-t border-border pb-20 md:pb-0">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <img src="/brand/kledio-logo.png" alt="KLEDIO" className="h-9 w-auto mb-8" loading="lazy" />
      <div className="grid md:grid-cols-5 gap-2 md:gap-8">
        {COLS.map((c) => <Col key={c.title} col={c} />)}
      </div>
      <div className="border-t border-border mt-10 pt-6 flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-sm font-bold mb-3">Folge KLEDIO</p>
          <div className="flex gap-2" aria-label="Social Media (Profile folgen bald)">
            {[["Instagram", Instagram], ["TikTok", Music2], ["Facebook", Facebook], ["YouTube", Youtube], ["LinkedIn", Linkedin]].map(([name, Icon]) => (
              <span key={name} title={`${name} – bald verfügbar`} aria-label={`${name} (bald verfügbar)`}
                className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground/60 cursor-default">
                <Icon className="w-4 h-4" aria-hidden="true" />
              </span>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-bold">KLEDIO App</p>
          <p className="text-xs text-muted-foreground mt-1">KLEDIO unterwegs nutzen.</p>
          <div className="flex gap-2 mt-2">
            <span className="text-xs font-semibold border border-border rounded-lg px-3.5 py-2 text-muted-foreground/60 cursor-default" title="Bald verfügbar">App Store · Bald verfügbar</span>
            <span className="text-xs font-semibold border border-border rounded-lg px-3.5 py-2 text-muted-foreground/60 cursor-default" title="Bald verfügbar">Google Play · Bald verfügbar</span>
          </div>
        </div>
      </div>
      <div className="border-t border-border mt-8 pt-6">
        <ul className="flex flex-wrap gap-x-5 gap-y-2">
          {[["Impressum", "/impressum"], ["Datenschutz", "/datenschutz"], ["AGB", "/agb"], ["Kontakt", "/kontakt"]].map(([label, to]) => (
            <li key={label}><Link to={to} className="text-xs text-muted-foreground hover:text-accent transition-colors">{label}</Link></li>
          ))}
          <li>
            <button data-testid="cookie-settings-btn" onClick={() => window.dispatchEvent(new Event("kledio:cookie-settings"))}
              className="text-xs text-muted-foreground hover:text-accent transition-colors">Cookie-Einstellungen</button>
          </li>
        </ul>
        <div className="mt-6" data-testid="footer-payment-methods">
          <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground mb-2.5">Sichere Zahlungsmethoden</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="h-8 px-3 bg-white border border-border rounded-lg flex items-center" data-testid="pay-icon-visa">
              <svg width="34" height="12" viewBox="0 0 48 16" aria-label="Visa"><text x="0" y="13" fontFamily="Arial, sans-serif" fontSize="15" fontWeight="bold" fontStyle="italic" fill="#1A1F71">VISA</text></svg>
            </span>
            <span className="h-8 px-3 bg-white border border-border rounded-lg flex items-center" data-testid="pay-icon-mastercard" aria-label="Mastercard">
              <svg width="30" height="18" viewBox="0 0 30 18"><circle cx="11" cy="9" r="8" fill="#EB001B" /><circle cx="19" cy="9" r="8" fill="#F79E1B" fillOpacity="0.9" /></svg>
            </span>
            <span className="h-8 px-3 rounded-lg flex items-center bg-[#FFB3C7]" data-testid="pay-icon-klarna">
              <span className="text-[12px] font-extrabold text-[#17120F] tracking-tight">Klarna.</span>
            </span>
            <span className="h-8 px-3 bg-[#1A1A1A] rounded-lg flex items-center gap-1" data-testid="pay-icon-applepay" aria-label="Apple Pay">
              <svg width="12" height="14" viewBox="0 0 384 512" fill="#fff"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" /></svg>
              <span className="text-[11px] font-semibold text-white">Pay</span>
            </span>
            <span className="h-8 px-3 bg-white border border-border rounded-lg flex items-center gap-1.5" data-testid="pay-icon-banktransfer">
              <Landmark className="w-3.5 h-3.5 text-foreground" />
              <span className="text-[11px] font-semibold">Banküberweisung</span>
            </span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-5" data-testid="footer-copyright">© {new Date().getFullYear()} KLEDIO. Alle Rechte vorbehalten.</p>
      </div>
    </div>
  </footer>
);
